import { useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useSlotGroup,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PropertyInfoPanel } from './PropertyInfoPanel';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const bgImageDef: SlotGroupDefinition = {
  label: 'Parallax Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Schedule a tour background',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// Address slot group — mirrors the Footer's address pattern so the visit-us
// card becomes a link to the property's Google Place page when set. The
// schema literal references this constant (extractor follows one level).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at
    // render time inside PropertyInfoPanel. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const prefix = 'scheduletourform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-tour-scheduler',
  });

  const bgImage = useSlotGroup('scheduletourform.bg_image', bgImageDef);

  // Parallax refs
  const parallaxSectionRef = useRef<HTMLDivElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = parallaxSectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      {/* Pinned parallax background + centered scheduler */}
      <div ref={parallaxSectionRef} className="stf-parallax-section">
        {/* Parallax background image */}
        <EditableSlotGroup groupPath="scheduletourform.bg_image" definition={bgImageDef}>
          <div ref={imgWrapRef} className="stf-parallax-img-wrap">
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                className="stf-parallax-img"
              />
            ) : (
              <div className="stf-parallax-img-fallback" />
            )}
          </div>
        </EditableSlotGroup>

        {/* Dark overlay for legibility */}
        <div className="stf-parallax-overlay" />

        {/* Centered form */}
        <div className="stf-parallax-content">
          <div className="stf-form-wrap">
            <EditableToken path={formBlockPath} as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <div className="stf-form-empty-card">
                  <p className="stf-form-empty">No form block selected</p>
                </div>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      {/* ── Section 2: Contact info tiles in a horizontal row ── */}
      <div className="stf-info-row-section">
        <div className="stf-info-row-container">
          <div className="stf-info-row-layout">
            <PropertyInfoPanel prefix={prefix} addressDefinition={addressLinkDef} />
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="scheduletourform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.visit_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup
        groupPath="scheduletourform.address"
        definition={addressLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableToken path="scheduletourform.call_label"   as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.phone"        as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        /* ── Parallax section ── */
        .stf-parallax-section {
          position: relative;
          overflow: hidden;
          min-height: 0;
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (min-width: 768px) {
          .stf-parallax-section {
            min-height: 520px;
            padding: 6rem 2rem;
          }
        }

        /* Oversized image wrapper gives the parallax translate room to move */
        .stf-parallax-img-wrap {
          position: absolute;
          inset-inline: 0;
          top: -20%;
          height: 140%;
          will-change: transform;
        }
        .stf-parallax-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .stf-parallax-img-fallback {
          width: 100%;
          height: 100%;
          background: var(--vibe-color-text, #1f2937);
        }

        .stf-parallax-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.58), rgba(0,0,0,0.48));
          pointer-events: none;
        }

        .stf-parallax-content {
          position: relative;
          z-index: 10;
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .stf-form-wrap {
          width: 100%;
          max-width: 640px;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
        }
        /* Uppercase the block's wizard heading */
        .stf-form-wrap h3 {
          text-transform: uppercase;
        }

        .stf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .stf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* ── Contact info row section ── */
        .stf-info-row-section {
          display: none;
          background: white;
          padding: 2rem 0;
          border-top: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        @media (min-width: 768px) {
          .stf-info-row-section { display: block; }
        }
        .stf-info-row-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        @media (min-width: 1024px) {
          .stf-info-row-container { padding: 0 2rem; }
        }

        /* Override info-cards to flow horizontally (PropertyInfoPanel uses .cf- classes) */
        .stf-info-row-layout .cf-info-cards {
          flex-direction: row;
          flex-wrap: wrap;
          background: transparent;
          border: none;
          border-radius: 0;
          overflow: visible;
          gap: 0.75rem;
        }
        .stf-info-row-layout .cf-card {
          flex: 1 1 180px;
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
          border-radius: var(--vibe-radius-lg, 12px);
          background: white;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
          padding: 0.75rem;
        }
        .stf-info-row-layout .cf-card:last-child {
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }

        @media (prefers-reduced-motion: reduce) {
          .stf-parallax-img-wrap { transform: none !important; }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduletourform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduletourform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduletourform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'scheduletourform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'scheduletourform.bg_image': bgImageDef,
    'scheduletourform.address': addressLinkDef,
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
